import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription, take } from 'rxjs';
import { APIResponse, AppData } from 'fakturnia-shared';
import { NotificationData } from 'fakturnia-shared';
import { ApiService } from 'shared/services/api.service';
import { SettingsService } from 'shared/services/settings.service';
import { ToastService } from 'shared/services/toast.service';
import { Utils } from 'fakturnia-shared';
import { GUSToDbClientConverter } from 'shared/utils/gus-to-db-client-converter';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatStepperModule } from '@angular/material/stepper';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { NumericOnlyDirective } from 'shared/directives/number-only.directive';
import { PhoneNumberDirective } from 'shared/directives/phone-number.directive';
import { AccountApiService } from 'shared/services/api/account.api.service';
import { AuthService } from 'shared/services/auth.service';

@Component({
  standalone: true,
  imports: [CommonModule, MatFormFieldModule, ReactiveFormsModule, MatButtonModule, MatInputModule, MatSelectModule, MatRadioModule, MatStepperModule, MatIconModule, NumericOnlyDirective, PhoneNumberDirective],
  selector: 'app-set-up-company-dialog',
  templateUrl: './set-up-company-dialog.component.html',
  styleUrls: ['./set-up-company-dialog.component.scss']
})
export class SetUpCompanyDialogComponent implements OnDestroy, AfterViewInit {

  subscriptions: Subscription[] = [];
  loading = false
  activeStep = 1

  accountNumbers:any = []
  selectedStepIndex = 0;
  countries = AppData.Countries;
  nipAvailable: boolean | null = null;
  checkingNip = false;
  
  firstForm = new FormGroup({
    nip: new FormControl('', [Validators.required, Validators.minLength(9)]),
    regon: new FormControl(''),
    name: new FormControl('', Validators.required),
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl('', Validators.required),
  })

  secondForm = new FormGroup({
    phoneNumber: new FormControl('', [Validators.required, Validators.minLength(9)]),
    street: new FormControl('', Validators.required),
    postalCode: new FormControl('', Validators.required),
    city: new FormControl('', Validators.required),
    country: new FormControl(''),
    countryCode: new FormControl('PL', [Validators.required]),
  })

  thirdForm = new FormGroup({
    bankAccountNumber: new FormControl('0', Validators.required),
  })

  dataLoadedFromGUS = false
  constructor(
    private apiService:ApiService,  
    private settingsService:SettingsService,  
    private dialogRef:MatDialogRef<SetUpCompanyDialogComponent>,
    private _toastService:ToastService,
    private _router:Router,
    private _accountApiService: AccountApiService,
    private _authService: AuthService
    )
  {
  }


  ngAfterViewInit(): void {
  }

  touchAll(form) {
    Object.keys(form.controls).forEach(key=> {
      form.controls[key].markAsTouched();
    })
  }

  onCountryChange(countryCode: string): void {
    const countryName = AppData.getCountryNameByCode(countryCode) || '';
    this.secondForm.controls.country.setValue(countryName);
  }

  nextStep() {

    if(this.selectedStepIndex == 0 && this.firstForm.invalid) { this.touchAll(this.firstForm); return;}
    if(this.selectedStepIndex == 1 && this.secondForm.invalid) {  this.touchAll(this.secondForm); return;}
    this.selectedStepIndex++;
    if(this.selectedStepIndex > 2) this.selectedStepIndex = 2
  }

  onStepSelect(e){
    this.selectedStepIndex = e.selectedIndex
  }

  search() {
    if(this.loading) return
    if(this.firstForm.controls['nip']?.errors?.['length'] > 0)  return

    this.loading = true;

    this.subscriptions.push(
      this.apiService.searchInGUS(this.firstForm.controls['nip'].value!)
    .pipe(take(1))
    .subscribe({
      next: (response:APIResponse) => {

        if(!response.data) {
          this.firstForm.controls['nip'].setErrors({
            notfound: true
          })
          this.loading = false
          return
        }

        if(typeof response.data.Nip != 'undefined') {

          this.dataLoadedFromGUS = true
          const company = GUSToDbClientConverter.convertOne(response.data)

          this.firstForm.controls['name'].setValue(company.name)
          this.firstForm.controls['regon'].setValue(company.regon)
          this.secondForm.controls['street'].setValue(company.street)
          this.secondForm.controls['postalCode'].setValue(company.postalCode)
          this.secondForm.controls['city'].setValue(company.city)
          this.activeStep = 2


          this.apiService.searchInMF(company.nip).pipe(take(1)).subscribe({
            next: (response:any)=> {

              if(Utils.isDefined(response,'data.accountNumbers')) {
                this.accountNumbers = response.data.accountNumbers;
                this.accountNumbers.forEach((number:any,index:number) => {
                    const bank:any = Utils.getBankByDigits(number)
                    let bankName = "Konto bankowe"
                    if(typeof bank.error == 'undefined') bankName = bank.bank
                    this.accountNumbers[index] = { bank: bankName, accountNumber: Utils.formatBankAccount(number)}
                });
                if(this.accountNumbers.length > 0) {
                  this.thirdForm.controls['bankAccountNumber'].setValue(this.accountNumbers[0].accountNumber)
                }
              }

              if(Utils.isDefined(response,'data.name')) {
                let result = Utils.extractNameInfo(response.data.name)
                if(result.firstName.length > 0) this.firstForm.controls['firstName'].setValue(result.firstName)
                if(result.lastName.length > 0) this.firstForm.controls['lastName'].setValue(result.lastName)
              }
            }
          })
        }


      },
      error: (err) => {
        this.loading = false
        this.activeStep = 1
      },
      complete: ()=> {
        this.loading = false
      }
    }))
  }

  close() {
    this.dialogRef.close()
  }

  logout() {
    this.close();
    this._authService.logout({ muted: true });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub=>sub.unsubscribe())
  }

  /**
   * Sprawdza dostępność NIP na blur pola NIP
   */
  checkNipOnBlur() {
    const nip = this.firstForm.controls['nip'].value;
    if (!nip || nip.length < 10) {
      this.nipAvailable = null;
      return;
    }

    this.checkingNip = true;
    this.nipAvailable = null;

    this._accountApiService.checkNipAvailability(nip)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.checkingNip = false;
          if (response.success && response.data) {
            this.nipAvailable = response.data.available;
            if (!response.data.available) {
              this.firstForm.controls['nip'].setErrors({ nipTaken: true });
            }
          }
        },
        error: () => {
          this.checkingNip = false;
          this.nipAvailable = null;
        }
      });
  }

  save() {
    if(this.firstForm.invalid || this.secondForm.invalid || this.thirdForm.invalid) {
      this._toastService.showToast(new NotificationData({
        title: 'Wystąpił błąd',
        status: 'warning',
        message: 'Formularz zawiera błędy, popraw go i spróbuj ponownie.',
      }))
      return
    }

    const body = {
      firstName: this.firstForm.value.firstName,
      lastName: this.firstForm.value.lastName,
      name: this.firstForm.value.name,
      nip: this.firstForm.value.nip,
      regon: this.firstForm.value.regon,
      phoneNumber: this.secondForm.value.phoneNumber,
      street: this.secondForm.value.street,
      postalCode: this.secondForm.value.postalCode,
      city: this.secondForm.value.city,
      country: this.secondForm.value.country,
      countryCode: this.secondForm.value.countryCode,
      defaultBankAccount: this.thirdForm.value.bankAccountNumber,
      bankAccounts: this.accountNumbers
    }

    this.apiService.completeRegister(body)
    .pipe(take(1))
    .subscribe({
      next: (data:any)=> {
        if(data.success) {
          this._toastService.showToast(new NotificationData({
            title: 'Konfiguracja konta',
            status: 'success',
            message: 'Konto zostało prawidłowo skonfigurowane.',
          }))
          this.settingsService.refreshSettings()
          this.dialogRef.close({ success: true })
        } else {
          this._toastService.showToast(new NotificationData({
            title: 'Wystąpił błąd',
            status: 'warning',
            message: 'Formularz zawiera błędy, popraw go i spróbuj ponownie.',
          }))
        } 
      }      
    })
  }
}
